'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'

// ---------------------------------------------------------------------------
// EntityContextCard
//
// Shown in the empty state of the AI Manager when the user is on a specific
// entity page (claim, sample, etc.). Replaces the generic quick-action bubbles
// with pre-seeded questions tailored to the entity being viewed.
// ---------------------------------------------------------------------------

interface EntityContextCardProps {
  entityType: string
  entityId: string
  onSelect: (query: string) => void
}

// Pre-seeded questions per entity type
const ENTITY_QUESTIONS: Record<string, string[]> = {
  claim: [
    'Summarize this claim\'s review history',
    'Is this claim\'s SLA at risk?',
    'Are there any open RFIs for this claim?',
    'Who has reviewed this claim so far?',
  ],
  sample: [
    'How many claims in this sample are still pending?',
    'Are there overdue claims in this sample?',
    'What is the SLA health for this sample?',
    'Are there any open RFIs in this sample?',
  ],
  rfi: [
    'Show me all open RFIs',
    'Which RFIs are overdue?',
    'RFI aging summary',
    'Show resolved RFIs this month',
  ],
  universe: [
    'What is the status of this universe?',
    'How many claims are in this universe file?',
    'Show me samples created from this universe',
    'Show me pending universes',
  ],
}

// Generic fallback if entity type is not in the map
const FALLBACK_QUESTIONS = [
  'Show me overdue claims',
  'SLA health summary',
  'Show open RFIs',
  'Reviewer workload summary',
]

// Entity type → icon
function getEntityIcon(entityType: string): React.ElementType {
  switch (entityType) {
    case 'claim':   return Icons.fileText
    case 'sample':  return Icons.layers
    case 'rfi':     return Icons.alertTriangle
    case 'universe': return Icons.inbox
    default:        return Icons.fileText
  }
}

// Entity type → display name
function getEntityLabel(entityType: string): string {
  return entityType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function EntityContextCard({ entityType, onSelect }: EntityContextCardProps) {
  const questions = ENTITY_QUESTIONS[entityType] ?? FALLBACK_QUESTIONS
  const EntityIcon = getEntityIcon(entityType)
  const typeLabel = getEntityLabel(entityType)

  return (
    <div className="flex h-full flex-col items-center justify-center text-center py-8 px-2">
      {/* Entity type badge */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={cn(
          'mb-5 flex h-16 w-16 items-center justify-center rounded-2xl',
          'bg-gradient-to-br from-brand-cornflower/20 to-brand-purple/20',
          'ring-1 ring-brand-cornflower/30',
        )}
      >
        <EntityIcon className="h-8 w-8 text-brand-cornflower" strokeWidth={1.5} />
      </motion.div>

      <motion.h3
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        className="font-display text-lg font-semibold text-brand-navy"
      >
        Viewing a {typeLabel}
      </motion.h3>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.3 }}
        className="mt-1 text-sm text-muted-foreground max-w-xs"
      >
        Here are some questions I can answer about this {typeLabel.toLowerCase()}:
      </motion.p>

      {/* Pre-seeded question chips */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.3 }}
        className="mt-6 flex flex-col gap-2 w-full max-w-sm"
      >
        {questions.map((q, i) => (
          <motion.button
            key={i}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + i * 0.06, duration: 0.25, ease: 'easeOut' }}
            type="button"
            onClick={() => onSelect(q)}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 rounded-xl',
              'bg-white border border-brand-cornflower/20',
              'text-sm text-brand-navy text-left',
              'shadow-sm',
              'transition-all duration-200',
              'hover:bg-brand-cornflower/8 hover:border-brand-cornflower/40 hover:shadow-md',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/40',
            )}
          >
            <Icons.messageSquare
              className="h-4 w-4 shrink-0 text-brand-cornflower opacity-70"
              strokeWidth={1.5}
            />
            <span className="flex-1">{q}</span>
            <Icons.arrowRight
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
              strokeWidth={2}
            />
          </motion.button>
        ))}
      </motion.div>
    </div>
  )
}
